/**
 * Error Message Templates and Localization System
 * Project Lighthouse - HIPAA-Compliant Healthcare Management
 * 
 * Provides dynamic error message templating with localization support,
 * context-aware messaging, and healthcare-specific user experience guidelines.
 */

// =============================================================================
// Types and Interfaces
// =============================================================================

export interface MessageTemplate {
  id: string;
  defaultMessage: string;
  description: string;
  variables?: string[];
  severity: 'critical' | 'high' | 'medium' | 'low' | 'info';
  category: string;
  userTypes: UserType[];
  contextTypes: ContextType[];
  actionSuggestions?: ActionSuggestion[];
}

export interface LocalizedMessage {
  en: string;
  es?: string;
  // Add more languages as needed
}

export interface MessageContext {
  userType: UserType;
  operation: string;
  component?: string;
  entityType?: 'patient' | 'employee' | 'supervision' | 'form';
  entityId?: string;
  timestamp?: Date;
  variables?: Record<string, any>;
}

export interface ActionSuggestion {
  label: string;
  action: string;
  primary?: boolean;
  requiresAuth?: boolean;
}

export type UserType = 'patient' | 'employee' | 'supervisor' | 'admin' | 'super_admin';
export type ContextType = 'form_submission' | 'data_retrieval' | 'authentication' | 'authorization' | 'system_error';

// =============================================================================
// Message Template Registry
// =============================================================================

export const MESSAGE_TEMPLATES: Record<string, MessageTemplate> = {
  // Authentication Messages
  'AUTH_INVALID_CREDENTIALS': {
    id: 'AUTH_INVALID_CREDENTIALS',
    defaultMessage: 'The email or password you entered is incorrect. Please try again.',
    description: 'User provided wrong login credentials',
    severity: 'medium',
    category: 'authentication',
    userTypes: ['patient', 'employee', 'supervisor', 'admin', 'super_admin'],
    contextTypes: ['authentication'],
    actionSuggestions: [
      { label: 'Try Again', action: 'retry', primary: true },
      { label: 'Forgot Password?', action: 'reset_password' }
    ]
  },

  'AUTH_ACCOUNT_LOCKED': {
    id: 'AUTH_ACCOUNT_LOCKED',
    defaultMessage: 'Your account has been temporarily locked for security reasons. Please try again in {{lockoutDuration}} or contact your administrator.',
    description: 'Account locked due to multiple failed attempts',
    variables: ['lockoutDuration'],
    severity: 'high',
    category: 'authentication',
    userTypes: ['patient', 'employee', 'supervisor', 'admin'],
    contextTypes: ['authentication'],
    actionSuggestions: [
      { label: 'Contact Support', action: 'contact_support', primary: true }
    ]
  },

  'AUTH_SESSION_EXPIRED': {
    id: 'AUTH_SESSION_EXPIRED',
    defaultMessage: 'Your session has expired for your security. Please log in again to continue.',
    description: 'User session has timed out',
    severity: 'medium',
    category: 'authentication',
    userTypes: ['patient', 'employee', 'supervisor', 'admin', 'super_admin'],
    contextTypes: ['authentication'],
    actionSuggestions: [
      { label: 'Log In Again', action: 'login', primary: true }
    ]
  },

  'AUTH_PASSWORD_CHANGE_REQUIRED': {
    id: 'AUTH_PASSWORD_CHANGE_REQUIRED',
    defaultMessage: 'You must change your password before continuing. This is required for your account security.',
    description: 'Password change is mandatory',
    severity: 'high',
    category: 'authentication',
    userTypes: ['patient', 'employee', 'supervisor', 'admin'],
    contextTypes: ['authentication'],
    actionSuggestions: [
      { label: 'Change Password', action: 'change_password', primary: true }
    ]
  },

  // Authorization Messages
  'AUTHZ_ACCESS_DENIED': {
    id: 'AUTHZ_ACCESS_DENIED',
    defaultMessage: 'You don\'t have permission to access this information. If you believe this is an error, please contact your administrator.',
    description: 'User lacks required permissions',
    severity: 'medium',
    category: 'authorization',
    userTypes: ['patient', 'employee', 'supervisor', 'admin'],
    contextTypes: ['authorization'],
    actionSuggestions: [
      { label: 'Go Back', action: 'go_back', primary: true },
      { label: 'Contact Admin', action: 'contact_admin' }
    ]
  },

  'AUTHZ_ADMIN_REQUIRED': {
    id: 'AUTHZ_ADMIN_REQUIRED',
    defaultMessage: 'This action requires administrator privileges. Please contact your system administrator.',
    description: 'Operation requires admin role',
    severity: 'medium',
    category: 'authorization',
    userTypes: ['patient', 'employee', 'supervisor'],
    contextTypes: ['authorization'],
    actionSuggestions: [
      { label: 'Contact Administrator', action: 'contact_admin', primary: true }
    ]
  },

  // Validation Messages
  'VAL_REQUIRED_FIELDS': {
    id: 'VAL_REQUIRED_FIELDS',
    defaultMessage: 'Please fill in all required fields marked with an asterisk (*) before continuing.',
    description: 'Required fields are missing',
    severity: 'low',
    category: 'validation',
    userTypes: ['patient', 'employee', 'supervisor', 'admin', 'super_admin'],
    contextTypes: ['form_submission'],
    actionSuggestions: [
      { label: 'Review Form', action: 'review_form', primary: true }
    ]
  },

  'VAL_INVALID_EMAIL': {
    id: 'VAL_INVALID_EMAIL',
    defaultMessage: 'Please enter a valid email address (for example: name@example.com).',
    description: 'Email format is incorrect',
    severity: 'low',
    category: 'validation',
    userTypes: ['patient', 'employee', 'supervisor', 'admin', 'super_admin'],
    contextTypes: ['form_submission'],
    actionSuggestions: [
      { label: 'Correct Email', action: 'fix_email', primary: true }
    ]
  },

  'VAL_PASSWORD_REQUIREMENTS': {
    id: 'VAL_PASSWORD_REQUIREMENTS',
    defaultMessage: 'Your password must be at least 12 characters long and include uppercase letters, lowercase letters, numbers, and special characters for security.',
    description: 'Password does not meet security requirements',
    severity: 'medium',
    category: 'validation',
    userTypes: ['patient', 'employee', 'supervisor', 'admin', 'super_admin'],
    contextTypes: ['form_submission'],
    actionSuggestions: [
      { label: 'Update Password', action: 'fix_password', primary: true },
      { label: 'Password Tips', action: 'show_password_help' }
    ]
  },

  'VALIDATION_DIRECT_MESSAGE': {
    id: 'VALIDATION_DIRECT_MESSAGE',
    defaultMessage: 'Validation error occurred during operation.',
    description: 'Direct validation message from server',
    severity: 'medium',
    category: 'validation',
    userTypes: ['patient', 'employee', 'supervisor', 'admin', 'super_admin'],
    contextTypes: ['form_submission'],
    actionSuggestions: [
      { label: 'Try Again', action: 'retry', primary: true },
      { label: 'Go Back', action: 'cancel' }
    ]
  },

  // Business Logic Messages
  'BIZ_PATIENT_INACTIVE': {
    id: 'BIZ_PATIENT_INACTIVE',
    defaultMessage: 'This action cannot be performed because the patient record is not currently active. Please check the patient status.',
    description: 'Operation attempted on inactive patient',
    severity: 'medium',
    category: 'business_logic',
    userTypes: ['employee', 'supervisor', 'admin', 'super_admin'],
    contextTypes: ['data_retrieval'],
    actionSuggestions: [
      { label: 'Check Patient Status', action: 'check_status', primary: true },
      { label: 'Contact Supervisor', action: 'contact_supervisor' }
    ]
  },

  'BIZ_FORM_LOCKED': {
    id: 'BIZ_FORM_LOCKED',
    defaultMessage: 'This form has been submitted and is now locked to protect the information. If you need to make changes, please contact your supervisor.',
    description: 'Form is locked after submission',
    severity: 'medium',
    category: 'business_logic',
    userTypes: ['employee', 'supervisor', 'admin'],
    contextTypes: ['form_submission'],
    actionSuggestions: [
      { label: 'View Form', action: 'view_form', primary: true },
      { label: 'Contact Supervisor', action: 'contact_supervisor' }
    ]
  },

  'BIZ_SCHEDULE_CONFLICT': {
    id: 'BIZ_SCHEDULE_CONFLICT',
    defaultMessage: 'This time slot overlaps with an existing appointment. Please choose a different time or check the schedule for availability.',
    description: 'Scheduling conflict detected',
    severity: 'medium',
    category: 'business_logic',
    userTypes: ['employee', 'supervisor', 'admin'],
    contextTypes: ['form_submission'],
    actionSuggestions: [
      { label: 'Choose Different Time', action: 'select_time', primary: true },
      { label: 'View Schedule', action: 'view_schedule' }
    ]
  },

  // System and Network Messages
  'SYS_CONNECTION_ERROR': {
    id: 'SYS_CONNECTION_ERROR',
    defaultMessage: 'Unable to connect to the server. Please check your internet connection and try again.',
    description: 'Network connection failure',
    severity: 'medium',
    category: 'system',
    userTypes: ['patient', 'employee', 'supervisor', 'admin', 'super_admin'],
    contextTypes: ['system_error'],
    actionSuggestions: [
      { label: 'Try Again', action: 'retry', primary: true },
      { label: 'Check Connection', action: 'check_connection' }
    ]
  },

  'SYS_SERVICE_UNAVAILABLE': {
    id: 'SYS_SERVICE_UNAVAILABLE',
    defaultMessage: 'The service is temporarily unavailable. We\'re working to restore it as quickly as possible. Please try again in a few minutes.',
    description: 'Service maintenance or overload',
    severity: 'high',
    category: 'system',
    userTypes: ['patient', 'employee', 'supervisor', 'admin', 'super_admin'],
    contextTypes: ['system_error'],
    actionSuggestions: [
      { label: 'Try Again Later', action: 'retry_later', primary: true },
      { label: 'Contact Support', action: 'contact_support' }
    ]
  },

  'SYS_OFFLINE_MODE': {
    id: 'SYS_OFFLINE_MODE',
    defaultMessage: 'You\'re currently offline. Your changes will be saved locally and synchronized when your connection is restored.',
    description: 'Device is offline',
    severity: 'info',
    category: 'system',
    userTypes: ['patient', 'employee', 'supervisor', 'admin', 'super_admin'],
    contextTypes: ['system_error'],
    actionSuggestions: [
      { label: 'Continue Offline', action: 'continue_offline', primary: true }
    ]
  },

  // HIPAA and Privacy Messages
  'HIPAA_ACCESS_DENIED': {
    id: 'HIPAA_ACCESS_DENIED',
    defaultMessage: 'Access to this protected health information is restricted due to privacy regulations. Contact your administrator if you need access.',
    description: 'HIPAA privacy protection triggered',
    severity: 'high',
    category: 'privacy',
    userTypes: ['employee', 'supervisor', 'admin'],
    contextTypes: ['authorization'],
    actionSuggestions: [
      { label: 'Contact Administrator', action: 'contact_admin', primary: true }
    ]
  },

  'HIPAA_CONSENT_REQUIRED': {
    id: 'HIPAA_CONSENT_REQUIRED',
    defaultMessage: 'Patient consent is required before accessing this information. Please ensure proper authorization is obtained.',
    description: 'Patient consent not provided',
    severity: 'high',
    category: 'privacy',
    userTypes: ['employee', 'supervisor', 'admin'],
    contextTypes: ['authorization'],
    actionSuggestions: [
      { label: 'Obtain Consent', action: 'get_consent', primary: true },
      { label: 'Contact Supervisor', action: 'contact_supervisor' }
    ]
  }
};

// =============================================================================
// Localized Message Bundles
// =============================================================================

export const LOCALIZED_MESSAGES: Record<string, LocalizedMessage> = {
  'AUTH_INVALID_CREDENTIALS': {
    en: 'The email or password you entered is incorrect. Please try again.',
    es: 'El correo electrónico o la contraseña que ingresó es incorrecta. Por favor, inténtelo de nuevo.'
  },

  'AUTH_ACCOUNT_LOCKED': {
    en: 'Your account has been temporarily locked for security reasons. Please try again in {{lockoutDuration}} or contact your administrator.',
    es: 'Su cuenta ha sido bloqueada temporalmente por razones de seguridad. Intente nuevamente en {{lockoutDuration}} o comuníquese con su administrador.'
  },

  'AUTH_SESSION_EXPIRED': {
    en: 'Your session has expired for your security. Please log in again to continue.',
    es: 'Su sesión ha expirado por su seguridad. Inicie sesión nuevamente para continuar.'
  },

  'VAL_REQUIRED_FIELDS': {
    en: 'Please fill in all required fields marked with an asterisk (*) before continuing.',
    es: 'Complete todos los campos obligatorios marcados con un asterisco (*) antes de continuar.'
  },

  'VAL_INVALID_EMAIL': {
    en: 'Please enter a valid email address (for example: name@example.com).',
    es: 'Ingrese una dirección de correo electrónico válida (por ejemplo: nombre@ejemplo.com).'
  },

  'VALIDATION_DIRECT_MESSAGE': {
    en: 'Validation error occurred during operation.',
    es: 'Error de validación ocurrió durante la operación.'
  },

  'SYS_CONNECTION_ERROR': {
    en: 'Unable to connect to the server. Please check your internet connection and try again.',
    es: 'No se puede conectar al servidor. Verifique su conexión a Internet e inténtelo de nuevo.'
  },

  'SYS_OFFLINE_MODE': {
    en: 'You\'re currently offline. Your changes will be saved locally and synchronized when your connection is restored.',
    es: 'Actualmente está desconectado. Sus cambios se guardarán localmente y se sincronizarán cuando se restablezca su conexión.'
  }
};

// =============================================================================
// Healthcare-Specific Message Guidelines
// =============================================================================

export const HEALTHCARE_MESSAGE_GUIDELINES = {
  // Patient-facing messages should be:
  PATIENT_GUIDELINES: {
    usePlainLanguage: true,
    avoidMedicalJargon: true,
    includeNextSteps: true,
    beReassuring: true,
    respectPrivacy: true
  },

  // Provider-facing messages can be:
  PROVIDER_GUIDELINES: {
    includeTechnicalDetails: true,
    useProperTerminology: true,
    includeErrorCodes: true,
    suggestSystemActions: true
  },

  // Admin messages should:
  ADMIN_GUIDELINES: {
    includeSystemContext: true,
    provideActionableSteps: true,
    includeSecurityImplications: true,
    enableEscalation: true
  }
};

// =============================================================================
// Message Template Processor
// =============================================================================

export class MessageTemplateProcessor {
  private locale: string = 'en';
  
  constructor(locale: string = 'en') {
    this.locale = locale;
  }

  /**
   * Process a message template with context and variables
   */
  public processMessage(
    templateId: string,
    context: MessageContext,
    variables?: Record<string, any>
  ): string {
    const template = MESSAGE_TEMPLATES[templateId];
    if (!template) {
      return this.getDefaultErrorMessage();
    }

    // Get localized message or fall back to default
    let message = this.getLocalizedMessage(templateId) || template.defaultMessage;

    // Substitute variables if provided
    if (variables && template.variables) {
      message = this.substituteVariables(message, variables);
    }

    // Apply context-specific modifications
    message = this.applyContextModifications(message, template, context);

    return message;
  }

  /**
   * Get message template with metadata
   */
  public getTemplate(templateId: string): MessageTemplate | null {
    return MESSAGE_TEMPLATES[templateId] || null;
  }

  /**
   * Get action suggestions for an error
   */
  public getActionSuggestions(templateId: string, context: MessageContext): ActionSuggestion[] {
    const template = MESSAGE_TEMPLATES[templateId];
    if (!template?.actionSuggestions) {
      return [];
    }

    // Filter suggestions based on user type and context
    return template.actionSuggestions.filter(suggestion => {
      // Add logic to filter suggestions based on user permissions
      if (suggestion.requiresAuth && context.userType === 'patient') {
        return false;
      }
      return true;
    });
  }

  /**
   * Set the current locale
   */
  public setLocale(locale: string): void {
    this.locale = locale;
  }

  /**
   * Get current locale
   */
  public getLocale(): string {
    return this.locale;
  }

  // =============================================================================
  // Private Helper Methods
  // =============================================================================

  private getLocalizedMessage(templateId: string): string | undefined {
    const localizedMessage = LOCALIZED_MESSAGES[templateId];
    if (!localizedMessage) {
      return undefined;
    }

    return localizedMessage[this.locale as keyof LocalizedMessage] || localizedMessage.en;
  }

  private substituteVariables(message: string, variables: Record<string, any>): string {
    return message.replace(/\{\{(\w+)\}\}/g, (match, variableName) => {
      return variables[variableName]?.toString() || match;
    });
  }

  private applyContextModifications(
    message: string,
    template: MessageTemplate,
    context: MessageContext
  ): string {
    // Apply user-type specific modifications
    switch (context.userType) {
      case 'patient':
        // Ensure patient messages are friendly and avoid technical terms
        if (HEALTHCARE_MESSAGE_GUIDELINES.PATIENT_GUIDELINES.beReassuring) {
          message = this.addReassurance(message, template.severity);
        }
        break;
      
      case 'admin':
      case 'super_admin':
        // Add technical context for administrators
        if (HEALTHCARE_MESSAGE_GUIDELINES.ADMIN_GUIDELINES.includeSystemContext) {
          message = this.addSystemContext(message, template);
        }
        break;
    }

    return message;
  }

  private addReassurance(message: string, severity: string): string {
    if (severity === 'critical' || severity === 'high') {
      return message + ' We\'re here to help resolve this issue.';
    }
    return message;
  }

  private addSystemContext(message: string, template: MessageTemplate): string {
    return message + ` [Error Code: ${template.id}]`;
  }

  private getDefaultErrorMessage(): string {
    const defaultMessages = {
      en: 'An unexpected error occurred. Please try again or contact support if the problem persists.',
      es: 'Ocurrió un error inesperado. Inténtelo de nuevo o comuníquese con el soporte si el problema persiste.'
    };
    
    return defaultMessages[this.locale as keyof typeof defaultMessages] || defaultMessages.en;
  }
}

// =============================================================================
// Export Default Instance
// =============================================================================

export const messageProcessor = new MessageTemplateProcessor();
export default MessageTemplateProcessor;